/**
 * Permissions Registry — SINGLE SOURCE OF TRUTH
 *
 * This is the ONLY file you need to edit when adding a new page or feature.
 * Everything else (routeScanner, Settings RBAC defaults, auto-discover) derives
 * from this file automatically.
 *
 * ADDING A NEW PAGE:
 *   1. Add a { pageName, requiredFeature } entry to APP_PAGES below.
 *   2. Add a ProtectedRoute with that pageName in AuthenticatedApp.js.
 *   3. Click "Auto-Discover" in Settings → Access Levels — done.
 *
 * ADDING A NEW FEATURE:
 *   1. Add the feature string to APP_FEATURES below.
 *   2. Click "Auto-Discover" — done.
 */

/**
 * All pages in the application
 * Format: { pageName: string, requiredFeature: string | null }
 */
export const APP_PAGES = [
  { pageName: 'dashboard', requiredFeature: 'viewDashboard' },
  { pageName: 'properties', requiredFeature: 'viewProperties' },
  { pageName: 'sales', requiredFeature: 'viewProperties' },
  { pageName: 'tenants', requiredFeature: 'viewTenants' },
  { pageName: 'landlords', requiredFeature: 'viewLandlords' },
  { pageName: 'maintenance', requiredFeature: 'viewMaintenance' },
  { pageName: 'finance', requiredFeature: 'viewFinance' },
  { pageName: 'reports', requiredFeature: 'viewReports' },
  { pageName: 'links', requiredFeature: 'viewLinks' },
  { pageName: 'broadcast', requiredFeature: 'sendBroadcasts' },
  { pageName: 'messages', requiredFeature: 'sendMessages' },
  { pageName: 'archive', requiredFeature: 'viewArchive' },
  { pageName: 'settings', requiredFeature: 'accessSettings' },
  { pageName: 'profile', requiredFeature: null },
  { pageName: 'account', requiredFeature: null },
  { pageName: 'notifications', requiredFeature: null },
  { pageName: 'compliance', requiredFeature: 'viewProperties' },
  { pageName: 'inspections', requiredFeature: 'viewInspections' },
  { pageName: 'inspectionReportBuilder', requiredFeature: 'viewInspections' },
  { pageName: 'contractors', requiredFeature: 'viewContractors' },
  { pageName: 'documents', requiredFeature: 'viewDocuments' },
  { pageName: 'companyDocuments', requiredFeature: 'viewDocuments' },
  { pageName: 'certificates', requiredFeature: 'viewCertificates' },
  { pageName: 'calendar', requiredFeature: 'viewCalendar' },
  { pageName: 'users', requiredFeature: 'viewUsers' },
  { pageName: 'audit', requiredFeature: 'viewAuditLogs' },
  { pageName: 'roles', requiredFeature: 'manageRoles' },
  { pageName: 'branding', requiredFeature: 'manageBranding' },
  { pageName: 'system', requiredFeature: 'systemConfig' },
  { pageName: 'payments', requiredFeature: 'viewPayments' },
  { pageName: 'enquiries', requiredFeature: 'viewEnquiries' },
  ...(process.env.REACT_APP_COMPANY_NAME === 'tog-plutoo'
    ? [{ pageName: 'scheduleD', requiredFeature: 'viewScheduleD' }]
    : [])
];

/**
 * All features in the application
 * These are the permission features that control access to functionality
 */
export const APP_FEATURES = [
  // Dashboard
  'viewDashboard',
  
  // Properties
  'viewProperties',
  'createProperties',
  'editProperties',
  'deleteProperties',
  'viewAllProperties',
  'archiveProperties',
  'restoreProperties',
  
  // Tenants
  'viewTenants',
  'viewAllTenants',
  'createTenants',
  'editTenants',
  'deleteTenants',
  'archiveTenants',
  'restoreTenants',
  
  // Landlords
  'viewLandlords',
  'viewAllLandlords',
  'viewOwnLandlords',
  'createLandlords',
  'editLandlords',
  'deleteLandlords',
  'archiveLandlords',
  'restoreLandlords',
  
  // Users
  'viewUsers',
  'createUsers',
  'editUsers',
  'deleteUsers',
  'manageAgents',
  
  // Roles & Permissions
  'manageRoles',
  'editRolePermissions',
  'editUserPermissions',
  
  // Finance
  'viewFinance',
  'viewPayments',
  'managePayments',
  'editFinance',
  
  // Maintenance
  'viewMaintenance',
  'createMaintenance',
  'updateMaintenance',
  'manageMaintenance',
  'editMaintenance',
  'deleteMaintenance',
  
  // Documents
  'viewDocuments',
  'uploadDocuments',
  'deleteDocuments',
  
  // Certificates
  'viewCertificates',
  'manageCertificates',
  
  // Communication
  'viewMessages',
  'sendMessages',
  'viewNotifications',
  'sendBroadcasts',
  
  // Links
  'viewLinks',
  'manageLinks',
  
  // Calendar
  'viewCalendar',
  'manageCalendar',
  
  // Inspections
  'viewInspections',
  'manageInspections',
  'inspections',

  // Finance Module (feature gate for finance/payments/reports routes)
  'finance_module',
  
  // Archive
  'viewArchive',
  'manageArchive',
  
  // Reports
  'viewReports',
  'exportData',
  'importExportData',
  
  // Contractors
  'viewContractors',
  'createContractors',
  'editContractors',
  'deleteContractors',
  
  // Enquiries
  'viewEnquiries',
  'createEnquiries',
  'editEnquiries',
  'deleteEnquiries',

  // System
  'accessSettings',
  'systemConfig',
  'viewAuditLogs',
  'manageBranding',
  ...(process.env.REACT_APP_COMPANY_NAME === 'tog-plutoo'
    ? ['viewScheduleD']
    : [])
];

/**
 * Get all page names from the registry
 */
export const getAllPageNames = () => {
  return APP_PAGES.map(page => page.pageName).filter(Boolean);
};

/**
 * TopBar quick link items (Properties, Maintenance, Compliance, Contacts)
 * Control visibility of these links in the TopBar for each role
 */
export const APP_TOP_BAR_LINKS = [
  { id: 'topBarProperties', label: 'Properties', path: '/properties', pageName: 'properties', requiredFeature: 'viewProperties' },
  { id: 'topBarSales', label: 'Sales', path: '/sales', pageName: 'sales', requiredFeature: 'viewProperties' },
  { id: 'topBarMaintenance', label: 'Maintenance', path: '/maintenance', pageName: 'maintenance', requiredFeature: 'viewMaintenance' },
  { id: 'topBarCompliance', label: 'Compliance', path: '/compliance', pageName: 'properties', requiredFeature: 'viewProperties' },
  { id: 'topBarContacts', label: 'Contacts', path: '/contacts', pageName: 'users', requiredFeature: 'viewUsers' }
];

/**
 * Get all TopBar link IDs from the registry
 */
export const getTopBarLinkIds = () => APP_TOP_BAR_LINKS.map(link => link.id);

/**
 * Get all feature names from the registry
 */
export const getAllFeatureNames = () => {
  const featuresFromPages = APP_PAGES
    .map(page => page.requiredFeature)
    .filter(Boolean);
  
  return [...new Set([...APP_FEATURES, ...featuresFromPages])];
};

